import type { Bcp47Locale } from '@backmarket/http-api'
import type { GetPickersResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'

import {
  DATE_FORMAT,
  DATE_FORMAT_SHORT,
  SHORT_DATE_FORMAT_BY_LOCALE,
} from './deliveryDate.constants'
import {
  TIMEZONE_BY_COUNTRY,
  TIMEZONE_BY_STATE_AU,
  TIMEZONE_BY_STATE_US,
} from './timezone.constants'

export const getNext1PMInSellerTimezone = (timezone: string | null) => {
  if (!timezone) return null

  // Get current time in seller's timezone
  // For some reason, the local has to be en-USg
  const nowInSellerTZ = new Date().toLocaleString('en-US', {
    timeZone: timezone,
  })

  const sellerDate = new Date(nowInSellerTZ)

  // Set to 1 PM today in seller's timezone
  const targetDate = new Date(sellerDate)
  targetDate.setHours(13, 0, 0, 0)

  // If 1 PM has already passed today, set to 1 PM tomorrow
  if (sellerDate.getTime() >= targetDate.getTime()) {
    targetDate.setDate(targetDate.getDate() + 1)
  }

  // Convert back to timestamp
  return targetDate.getTime() - sellerDate.getTime()
}

export const getTimezone = (
  merchant: NonNullable<GetPickersResponse['selectedOffer']>['merchant'],
) => {
  if (merchant?.country) {
    if (merchant.country.code === 'US') {
      return TIMEZONE_BY_STATE_US[merchant.state]
    }

    if (merchant.country.code === 'AU') {
      return TIMEZONE_BY_STATE_AU[merchant.state]
    }

    return TIMEZONE_BY_COUNTRY[merchant.country.code]
  }

  return null
}

export const getFormattedTime = (
  targetTime: number | null,
  currentLocale: Bcp47Locale,
) => {
  if (!targetTime) return null

  const totalSeconds = Math.floor(targetTime / 1000)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)

  // @ts-expect-error - DurationFormat not available in current TS version
  const formatter = new Intl.DurationFormat(currentLocale, {
    hours: 'long',
    minutes: 'long',
  })

  return formatter.format({ hours, minutes })
}

export const getShortDate = (
  date: Date,
  currentLocale: Bcp47Locale,
  format: 'earliest' | 'lastest',
) => {
  if (SHORT_DATE_FORMAT_BY_LOCALE[currentLocale] === format) {
    return date.toLocaleDateString(currentLocale, DATE_FORMAT_SHORT)
  }

  return date.toLocaleDateString(currentLocale, DATE_FORMAT)
}

export function formatDeliveryDateRange(
  earliestDate: Date,
  latestDate: Date,
  locale: Bcp47Locale,
): { earliest: string; latest: string } {
  if (latestDate.getMonth() !== earliestDate.getMonth()) {
    return {
      earliest: earliestDate.toLocaleDateString(locale, DATE_FORMAT),
      latest: latestDate.toLocaleDateString(locale, DATE_FORMAT),
    }
  }

  return {
    earliest: getShortDate(earliestDate, locale, 'lastest'),
    latest: getShortDate(latestDate, locale, 'earliest'),
  }
}
